import React, { useEffect, useState } from "react";
import * as XLSX from "sheetjs-style";

import {
  CCard,
  CCardBody,
  CCardHeader,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHeaderCell,
  CTableRow,
  CButton,
  CInputGroup,
  CInputGroupText,
  CFormInput,
  CPagination,
  CFormSelect,
  CBadge,
} from "@coreui/react";
import { cilPencil, cilTrash, cilCloudDownload } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import useApi from "../../../config/useApi";
import { CSVLink } from "react-csv";

import EnhancedChallanModal from "./ChallanDetailsModal";

const ChallanTable = () => {
  const { Get, Delete, getHost } = useApi();
  const [state, setState] = useState({
    isLoading: false,
    exportCustomer: "",
    exportTimePeriod: "",
    openingBalance: 0,
    closingBalance: 0,
    searchQuery: "",
    currentPage: 1,
    challansPerPage: 100,
    showSuggestions: false,
    showModal: false,
    selectedChallan: null,
    totalDifference: 0,
    grandTotal: 0,
    customerDetails: null
  });
  const [challans, setChallans] = useState([]);
  const [filteredChallans, setFilteredChallans] = useState([]);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  
  const customerList = [...new Set(challans.map(c => c.customer_name).filter(Boolean))];

  const fetchChallans = async () => {
  try {
    const data = await Get("challans");
  const sortedData = data.results.sort((a, b) => {
  const numA = parseInt(a.challan_number.replace(/\D/g, "")) || 0;
  const numB = parseInt(b.challan_number.replace(/\D/g, "")) || 0;
  return numB - numA; // Sort from newest (largest number) to oldest
});

    setChallans(sortedData);
    setFilteredChallans(sortedData);
  } catch (error) {
    console.error("Error fetching challans:", error);
  }
};


  useEffect(() => {
    fetchChallans();
  }, []);

  const safeParseNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const calculateBalances = (challans, timePeriod) => {
    if (!timePeriod) return { 
      openingBalance: 0, 
      closingBalance: 0, 
      totalDifference: 0,
      grandTotal: 0
    };

    const now = new Date();
    let startDate, endDate;

    if (timePeriod === "month") {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else if (timePeriod === "year") {
      startDate = new Date(now.getFullYear(), 0, 1);
      endDate = new Date(now.getFullYear(), 11, 31);
    }

    // Calculate opening balance (sum of all transactions before the start date)
    const openingBalance = challans
      .filter(challan => {
        const matchesCustomer = state.exportCustomer 
          ? challan.customer_name?.toLowerCase() === state.exportCustomer.toLowerCase()
          : true;
        const challanDate = new Date(challan.challan_date);
        return matchesCustomer && challanDate < startDate;
      })
      .reduce((sum, challan) => sum + safeParseNumber(challan.total_amount), 0);

    // Calculate closing balance (sum of all transactions up to the end date)
    const closingBalance = challans
      .filter(challan => {
        const matchesCustomer = state.exportCustomer 
          ? challan.customer_name?.toLowerCase() === state.exportCustomer.toLowerCase()
          : true;
        const challanDate = new Date(challan.challan_date);
        return matchesCustomer && challanDate <= endDate;
      })
      .reduce((sum, challan) => sum + safeParseNumber(challan.total_amount), 0);

    // Calculate total difference (sum of difference amounts before the start date)
    const totalDifference = challans
      .filter(challan => {
        const matchesCustomer = state.exportCustomer 
          ? challan.customer_name?.toLowerCase() === state.exportCustomer.toLowerCase()
          : true;
        const challanDate = new Date(challan.challan_date);
        return matchesCustomer && challanDate < startDate;
      })
      .reduce((sum, challan) => sum + safeParseNumber(challan.difference_amount), 0);

    // Calculate grand total (sum of all amounts in the selected period)
    const grandTotal = challans
      .filter(challan => {
        const matchesCustomer = state.exportCustomer 
          ? challan.customer_name?.toLowerCase() === state.exportCustomer.toLowerCase()
          : true;
        const challanDate = new Date(challan.challan_date);
        return matchesCustomer && challanDate >= startDate && challanDate <= endDate;
      })
      .reduce((sum, challan) => sum + safeParseNumber(challan.total_amount), 0);

    return { openingBalance, closingBalance, totalDifference, grandTotal };
  };

  const updateBalances = () => {
    if (!state.exportTimePeriod) {
      setState(prev => ({
        ...prev,
        openingBalance: 0,
        closingBalance: 0,
        totalDifference: 0,
        grandTotal: 0
      }));
      return;
    }

    const { openingBalance, closingBalance, totalDifference, grandTotal } = calculateBalances(challans, state.exportTimePeriod);

    setState(prev => ({
      ...prev,
      openingBalance,
      closingBalance,
      totalDifference,
      grandTotal
    }));
  };

  useEffect(() => {
    updateBalances();
  }, [state.exportCustomer, state.exportTimePeriod, challans]);

  const handleDelete = async (event, id) => {
    event.preventDefault();
    if (!window.confirm("Are you sure you want to delete this challan?")) return;

    try {
      await Delete("challans", id);
      const updatedChallans = challans.filter((challan) => challan.id !== id);
      setChallans(updatedChallans);
      setFilteredChallans(updatedChallans);
    } catch (error) {
      console.error("Error deleting challan:", error);
    }
  };

  const handleEdit = async (challan) => {
    const details = await Get(`${getHost()}/api/v1/challans/${challan.id}/`);
    setState(prev => ({
      ...prev,
      selectedChallan: {
        ...details,
        products: details.products.map((p) => ({
          ...p,
          selected_category: p.selected_category,
        })),
      },
      customerDetails: {
        id: details.customer,
        name: details.customer_name,
        address: details.customer_address,
        phone: details.customer_phone,
        careof: details.careof,
      },
      showModal: true
    }));
  };

  const handleModalClose = async (refresh = false) => {
    setState(prev => ({...prev, showModal: false, selectedChallan: null, customerDetails: null}));
    if (refresh) await fetchChallans();
  };

  const handleAddNew = () => {
    setState(prev => ({...prev, showModal: true, selectedChallan: null, customerDetails: null}));
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setState(prev => ({...prev, searchQuery: query}));
    const filtered = challans.filter(
      (challan) =>
        challan.challan_number.toLowerCase().includes(query) ||
        challan.customer_name?.toLowerCase().includes(query)
    );
    setFilteredChallans(filtered);
    setState(prev => ({...prev, currentPage: 1}));

    const suggestions = [
      ...new Set(
        challans
          .filter((c) => c.customer_name?.toLowerCase().includes(query))
          .map((c) => c.customer_name)
      ),
    ];
    setCustomerSuggestions(suggestions.slice(0, 5));
    setState(prev => ({...prev, showSuggestions: query.length > 0}));
  };

  const handleSuggestionClick = (name) => {
    setState(prev => ({...prev, searchQuery: name, showSuggestions: false}));
    const filtered = challans.filter((c) =>
      c.customer_name?.toLowerCase().includes(name.toLowerCase())
    );
    setFilteredChallans(filtered);
    setState(prev => ({...prev, currentPage: 1}));
  };

  const handleChallansPerPageChange = (e) => {
    setState(prev => ({...prev, challansPerPage: Number(e.target.value), currentPage: 1}));
  };

  const handleExportCustomerChange = (e) => setState(prev => ({...prev, exportCustomer: e.target.value}));
  
  const handleTimePeriodChange = (e) => {
    setState(prev => ({...prev, exportTimePeriod: e.target.value}));
  };

  const indexOfLastChallan = state.currentPage * state.challansPerPage;
  const indexOfFirstChallan = indexOfLastChallan - state.challansPerPage;
  const currentChallans = filteredChallans.slice(indexOfFirstChallan, indexOfLastChallan);

  const paginate = (page) => setState(prev => ({...prev, currentPage: page}));

  const formatCurrency = (value) => {
    const num = safeParseNumber(value);
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const getChallanProductNames = (products) => {
    return products.map(p => p.product_name).join(", ");
  };

  const getFilteredExportData = () => {
    const now = new Date();
    let startDate, endDate;

    if (state.exportTimePeriod === "month") {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else if (state.exportTimePeriod === "year") {
      startDate = new Date(now.getFullYear(), 0, 1);
      endDate = new Date(now.getFullYear(), 11, 31);
    }

    return filteredChallans.filter((challan) => {
      const matchCustomer = state.exportCustomer
        ? challan.customer_name?.toLowerCase().includes(state.exportCustomer.toLowerCase())
        : true;
      
      const challanDate = new Date(challan.challan_date);
      let matchDate = true;
      
      if (state.exportTimePeriod) {
        matchDate = challanDate >= startDate && challanDate <= endDate;
      }
      
      return matchCustomer && matchDate;
    });
  };

const exportToExcel = () => {
  const wb = XLSX.utils.book_new();

  const safeParseNumber = (val) => isNaN(parseFloat(val)) ? 0 : parseFloat(val);
  const round2 = (num) => Math.round(num * 100) / 100;

  const parseDate = (d) => {
    if (!d) return new Date("Invalid");
    const parts = d.split(/[-/]/);
    if (parts.length === 3 && parts[2].length === 4) {
      return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    }
    return new Date(d);
  };

  const formatDate = (d) => {
    const date = parseDate(d);
    return isNaN(date.getTime()) ? d : date.toLocaleDateString();
  };

  const getChallanTotal = (c) =>
    round2(
      safeParseNumber(c.total_amount) +
      safeParseNumber(c.freight_amount) +
      safeParseNumber(c.tax_amount)
    );

  const getProductNames = (products = []) =>
    products.map(p => p.product_name).join(", ");

  const totalAmount = (challans) =>
    round2(challans.reduce((sum, c) => sum + getChallanTotal(c), 0));

  const numericCell = (val) => ({
    v: val,
    t: "n",
    z: "#,##0.00",
  });

  const now = new Date();
  const currentYear = now.getFullYear();

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const filtered = getFilteredExportData();

  const buildMonthBlock = (label, monthIndex, year) => {
    const monthStart = new Date(year, monthIndex, 1);
    const monthEnd = new Date(year, monthIndex + 1, 0);

    const inwardChallans = filtered.filter(c => {
      const cd = parseDate(c.challan_date);
      return c.challan_type?.toLowerCase() === "inward" && cd >= monthStart && cd <= monthEnd;
    });

    const outwardChallans = filtered.filter(c => {
      const cd = parseDate(c.challan_date);
      return c.challan_type?.toLowerCase() === "outward" && cd >= monthStart && cd <= monthEnd;
    });

    const inwardRows = inwardChallans.map(c => [
      formatDate(c.challan_date),
      c.challan_number,
      getProductNames(c.products),
      numericCell(getChallanTotal(c)),
    ]);

    const outwardRows = outwardChallans.map(c => [
      numericCell(getChallanTotal(c)),
      getProductNames(c.products),
      c.challan_number,
      formatDate(c.challan_date),
    ]);

    const maxLength = Math.max(inwardRows.length, outwardRows.length);
    const tableRows = [];

    for (let i = 0; i < maxLength; i++) {
      const left = inwardRows[i] || ["", "", "", ""];
      const right = outwardRows[i] || ["", "", "", ""];
      tableRows.push([...left, "", ...right]);
    }

    const inwardTotal = totalAmount(inwardChallans);
    const outwardTotal = totalAmount(outwardChallans);
return [
  [label],
  ["Challan Date", "Challan No.", "Products", "Amount", "", "Amount", "Products", "Challan No.", "Date"],
  ["", "", "", "", "", numericCell(state.openingBalance), "Opening Balance", "", ""],
  ...tableRows,
  [],
  ["", "", "Total Inward", numericCell(inwardTotal), "", numericCell(outwardTotal), "Total Outward", "", ""],
  []
];

  };

  let sheetData = [
    ["Challan Detailed Report"],
    [],
    ["Customer", state.exportCustomer || "All Customers"],
    [],
  ];

  for (let m = 0; m < 12; m++) {
    const block = buildMonthBlock(months[m], m, currentYear);
    if (block.length > 10) {
      sheetData = [...sheetData, ...block];
    }
  }

  const allInward = filtered.filter(c => c.challan_type?.toLowerCase() === "inward");
  const allOutward = filtered.filter(c => c.challan_type?.toLowerCase() === "outward");

  const totalInward = totalAmount(allInward);
  const totalOutward = totalAmount(allOutward);
  const openingBalance = round2(safeParseNumber(state.openingBalance));
  const closingBalance = round2(openingBalance + totalOutward - totalInward);

  sheetData.push(
    ["Opening Balance Amount", "", "", numericCell(openingBalance)],
    ["Total Inward Amount", "", numericCell(totalInward)],
    ["Total Outward Amount", "", "", numericCell(totalOutward)],
    ["Closing Balance Amount", "", "", numericCell(closingBalance)]
  );

  // ✅ Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(sheetData);

  // ✅ Column width formatting
  ws["!cols"] = [
    { wch: 12 }, { wch: 20 }, { wch: 30 }, { wch: 15 }, { wch: 2 },
    { wch: 15 }, { wch: 30 }, { wch: 20 }, { wch: 15 }
  ];

  // ✅ Apply all borders
  const applyBordersToSheet = (ws, range) => {
    const borderStyle = {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } },
    };

    const XLSXRange = XLSX.utils.decode_range(range);
    for (let R = XLSXRange.s.r; R <= XLSXRange.e.r; ++R) {
      for (let C = XLSXRange.s.c; C <= XLSXRange.e.c; ++C) {
        const cell_address = { c: C, r: R };
        const cell_ref = XLSX.utils.encode_cell(cell_address);
        if (!ws[cell_ref]) continue;
        if (!ws[cell_ref].s) ws[cell_ref].s = {};
        ws[cell_ref].s.border = borderStyle;
      }
    }
  };

  const maxRow = sheetData.length;
  const maxCol = Math.max(...sheetData.map(row => row.length));
  const range = `A1:${XLSX.utils.encode_cell({ r: maxRow - 1, c: maxCol - 1 })}`;
  applyBordersToSheet(ws, range);

  XLSX.utils.book_append_sheet(wb, ws, "Challan Report");

  XLSX.writeFile(
    wb,
    `challans_${state.exportCustomer || "all"}_${state.exportTimePeriod || "all"}.xlsx`
  );
};



  return (
    <div className="challan-table-container">
      <CCard>
        <CCardHeader><h4>Challan Management</h4></CCardHeader>
        <CCardBody>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start mb-4">
            <CButton color="primary" onClick={handleAddNew} className="mb-2 mb-md-0 me-md-2">
              Add New Challan
            </CButton>

            <div style={{ position: "relative", width: "100%" }}>
              <CInputGroup className="mb-2 mb-md-0 w-100">
                <CInputGroupText>🔍</CInputGroupText>
                <CFormInput
                  value={state.searchQuery}
                  onChange={handleSearch}
                  placeholder="Search challans..."
                  onFocus={() => setState(prev => ({...prev, showSuggestions: state.searchQuery.length > 0}))}
                  onBlur={() => setTimeout(() => setState(prev => ({...prev, showSuggestions: false})), 150)}
                />
              </CInputGroup>
              {state.showSuggestions && customerSuggestions.length > 0 && (
                <ul className="list-group position-absolute w-100 z-index-3">
                  {customerSuggestions.map((name, i) => (
                    <li
                      key={i}
                      className="list-group-item list-group-item-action"
                      onClick={() => handleSuggestionClick(name)}
                      style={{ cursor: "pointer" }}
                    >
                      {name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="d-flex flex-column flex-md-row gap-2 mb-2 align-items-center">
            <CFormSelect
              value={state.exportCustomer}
              onChange={handleExportCustomerChange}
              className="flex-grow-1"
            >
              <option value="">Filter by customer</option>
              {customerList.map((name, i) => (
                <option key={i} value={name}>{name}</option>
              ))}
            </CFormSelect>
            
            <CFormSelect
              value={state.exportTimePeriod}
              onChange={handleTimePeriodChange}
              className="flex-grow-1"
            >
              <option value="">All Time Periods</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
            </CFormSelect>
            
            <CButton 
              color="success" 
              onClick={exportToExcel}
              disabled={state.isLoading}
            >
              <CIcon icon={cilCloudDownload} className="me-1" />
              Export Excel
            </CButton>
          </div>
          
          <div className="d-flex flex-wrap align-items-start gap-3 bg-light p-2 rounded mb-3">
            <div>
              <strong className="me-2">Opening Balance:</strong>
              <CBadge color="info">{formatCurrency(state.openingBalance)}</CBadge>
            </div>
            <div>
              <strong className="me-2">Closing Balance:</strong>
              <CBadge color="primary">{formatCurrency(state.closingBalance)}</CBadge>
            </div>
           
          
          </div>

          <div className="table-responsive">
            <CTable striped hover>
              <thead>
                <tr>
                  <CTableHeaderCell>No</CTableHeaderCell>
                  <CTableHeaderCell>Challan Number</CTableHeaderCell>
                  <CTableHeaderCell>Customer</CTableHeaderCell>
                  <CTableHeaderCell>Date</CTableHeaderCell>
                  <CTableHeaderCell>Invoice #</CTableHeaderCell>
                  <CTableHeaderCell>Invoice Amount</CTableHeaderCell>
                  <CTableHeaderCell>Total</CTableHeaderCell>
                  <CTableHeaderCell>Actions</CTableHeaderCell>
                </tr>
              </thead>
              <CTableBody>
                {currentChallans.map((c, idx) => (
                  <CTableRow key={c.id}>
                    <CTableDataCell>{indexOfFirstChallan + idx + 1}</CTableDataCell>
                    <CTableDataCell>{c.challan_number}</CTableDataCell>
                    <CTableDataCell>{c.customer_name}</CTableDataCell>
                    <CTableDataCell>{new Date(c.challan_date).toLocaleDateString()}</CTableDataCell>
                    <CTableDataCell>{c.invoice_number}</CTableDataCell>
                    <CTableDataCell>{formatCurrency(c.invoice_amount)}</CTableDataCell>
                    <CTableDataCell>{formatCurrency(c.total_amount)}</CTableDataCell>
                    <CTableDataCell>
                      <CButton color="warning" size="sm" onClick={() => handleEdit(c)}>
                        <CIcon icon={cilPencil} />
                      </CButton>{" "}
                      <CButton color="danger" size="sm" onClick={(e) => handleDelete(e, c.id)}>
                        <CIcon icon={cilTrash} />
                      </CButton>
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <span>
              Showing {indexOfFirstChallan + 1} to {Math.min(indexOfLastChallan, filteredChallans.length)} of {filteredChallans.length}
            </span>
            <CPagination>
              {[...Array(Math.ceil(filteredChallans.length / state.challansPerPage))].map((_, i) => (
                <CButton
                  key={i}
                  color={state.currentPage === i + 1 ? "primary" : "secondary"}
                  onClick={() => paginate(i + 1)}
                >
                  {i + 1}
                </CButton>
              ))}
            </CPagination>
          </div>
        </CCardBody>
      </CCard>

      {state.showModal && (
        <EnhancedChallanModal
          show={state.showModal}
          onClose={handleModalClose}
          challan={state.selectedChallan}
          customerDetails={state.customerDetails}
          onRefresh={fetchChallans}
        />
      )}
    </div>
  );
};

export default ChallanTable;